import {
  ConfirmDialog,
  Div,
  Flex,
  LoadingSpinner,
  Text,
} from '@sendoutcards/quantum-design-ui'
import React, { FC } from 'react'
import { useActions, useSelector, useState } from 'src/hooks'
import { AddOrderCardRoute } from 'src/orders/routes/AddOrderCardRoute'
import { Welcome } from 'src/dashboard/components/Widgets/Reminders/components/Welcome'
import { WeekDayView } from 'src/dashboard/components/Widgets/Reminders/components/WeekDayView'
import { Widget } from 'src/dashboard/components/Widgets/Widget'
import { ReminderListCard } from 'src/dashboard/components/Widgets/Reminders/components/ReminderListCard'
import { Button } from 'src/design_system/components/Button/Button'
import { TabBar } from 'src/design_system/components/TabBar/TabBar'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import {
  useCreateOrder,
  useReminders,
  useUpdateReminder,
} from 'src/react_query'
import {
  DayInfoType,
  useGetCurrentWeekDays,
} from 'src/dashboard/hooks/useCurrentWeekDays'
import { MinimalReminderFragment } from 'src/graphql/generated/graphql'

type RemindersListWidgetProps = {
  firstName?: string
  isLandscape?: boolean
}

export const RemindersListWidget: FC<RemindersListWidgetProps> = ({
  firstName,
  isLandscape,
}) => {
  const [selectedReminder, setSelectedReminder] = useState<
    MinimalReminderFragment | undefined
  >()
  const [showNoRemindersDialog, setShowNoRemindersDialog] = useState(false)
  const [remindersTimeframe, setRemindersTimeframe] = useState<'30' | '90'>(
    '30',
  )
  const actions = useActions()
  const createOrder = useCreateOrder()
  const updateReminder = useUpdateReminder()

  const windowWidth = useSelector(state => state.window.width)
  const isMobile = windowWidth <= 574
  const isWide = windowWidth > 1260
  const isVerticalLayout = !isLandscape

  const currentWeekDays: DayInfoType[] = useGetCurrentWeekDays()
  const currentDay = currentWeekDays.find(day => day.isToday)

  const { data, isLoading } = useReminders({
    offset: 0,
    limit: 5,
  })

  const reminders = data?.reminders.filter(
    reminder => !(reminder.type === 'PROJECT' && !reminder.campaign),
  )
  const hasReminders = Boolean(reminders?.length)

  const upcomingRangeDays = remindersTimeframe === '90' ? 90 : 30

  const normalizeEventDate = (eventDate: string): Date | null => {
    if (!eventDate) return null
    const parsedDate = eventDate.includes('T')
      ? new Date(eventDate)
      : new Date(`${eventDate}T00:00:00Z`)

    if (Number.isNaN(parsedDate.getTime())) return null

    return new Date(
      Date.UTC(
        parsedDate.getUTCFullYear(),
        parsedDate.getUTCMonth(),
        parsedDate.getUTCDate(),
      ),
    )
  }

  const isWithinUpcomingRange = (eventDate: string): boolean => {
    const today = new Date()
    const todayUtc = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
    )
    const eventUtc = normalizeEventDate(eventDate)
    if (!eventUtc) return false
    const diffMs = eventUtc.getTime() - todayUtc.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    return diffDays >= 0 && diffDays <= upcomingRangeDays
  }

  const remindersWithinRange = (reminders ?? []).filter(reminder =>
    isWithinUpcomingRange(reminder.eventDate ?? ''),
  )
  const remindersToShow = remindersWithinRange.slice(0, 5)
  const hasAnyListItems = remindersToShow.length > 0
  const shouldCenterAddButton = !hasReminders
  const shouldShowViewAllButton = hasReminders

  const reminderTabs = [
    { title: '30 Days', onClick: () => setRemindersTimeframe('30') },
    { title: '90 Days', onClick: () => setRemindersTimeframe('90') },
  ]

  const handleAddReminderClick = () => {
    if (!hasReminders) {
      setShowNoRemindersDialog(true)
      return
    }
    actions.openNotifications()
  }
  const handleCreateOrder = async (
    contact: { id: string },
    reminderId: string,
  ) => {
    const {
      createOrder: { order },
    } = await createOrder.mutateAsync({
      order: { contacts: [contact.id] },
    })

    await updateReminder.mutateAsync({
      reminder: { id: reminderId, orderId: order.id },
    })
    actions.openOrder(order.id, AddOrderCardRoute())
  }

  const weekDayEvents = remindersWithinRange.map(
    reminder => reminder.eventDate ?? '',
  )

  const getGreeting = (firstName: string | null | undefined): string => {
    const currentHour = new Date().getHours()
    let mutableGreeting

    if (currentHour < 12) {
      mutableGreeting = 'Good Morning'
    } else if (currentHour < 18) {
      mutableGreeting = 'Good Afternoon'
    } else {
      mutableGreeting = 'Good Evening'
    }

    return firstName ? `${mutableGreeting}, ${firstName}` : mutableGreeting
  }

  return (
    <Flex
      width="100%"
      flexDirection={isLandscape ? 'row' : 'column'}
      inset="x2"
      backgroundColor="foreground"
      style={{
        borderRadius: 32,
        boxShadow: 'rgba(0, 0, 0, 0.04) 0px 4px 32px',
        flex: isWide ? 1 : undefined,
        minHeight: 0,
        height: isWide ? '100%' : undefined,
      }}
      overflowY="hidden"
    >
      <Flex outset={{ right: isLandscape ? '24px' : undefined }}>
        <Welcome
          shouldStackGreeting={isLandscape || isMobile}
          isMobile={isMobile}
          backgroundColor="#d4e9ebd6"
          graphicUrl="https://soc-website-public.s3.us-west-2.amazonaws.com/static/soc-website/images/Card.png"
          title={getGreeting(firstName)}
          description={'Share some gratitude today!'}
          action={{
            title: 'Send a Card',
            onClick: () => actions.openCatalog(),
          }}
        />
      </Flex>
      <Flex
        flexDirection="column"
        inset={{ horizontal: 'x_75' }}
        width="100%"
        style={{ minHeight: 0, flex: 1 }}
      >
        <Flex
          flexDirection="column"
          width="100%"
          overflowY="auto"
          style={{ flex: 1, minHeight: 0 }}
        >
          <Flex overflowY="auto" width="100%">
            <WeekDayView weekDayEventList={weekDayEvents} isMobile={isMobile} />
          </Flex>
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems="flex-start"
            columnGap="x4"
          >
            <Text
              type="caption"
              weight="bold"
              color="primaryHeading"
              content="Upcoming Reminders"
              style={{ marginLeft: 4, lineHeight: 1.2, paddingBottom: 16 }}
            />
            <TabBar
              tabs={reminderTabs}
              activeTabIndex={remindersTimeframe === '90' ? 1 : 0}
              height="32px"
              radius="999px"
              barBackgroundColor="#fff"
              activeTabBackgroundColor="#BE9CDD"
              activeTabTextColor="#FFF"
              barBoxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.08)"
              tabTextSize="11px"
              tabInsetVertical="x_25"
            />
          </Flex>

          {isLoading ? (
            <Div style={{ marginTop: 16, marginBottom: 16 }}>
              <LoadingSpinner size="large" />
            </Div>
          ) : !hasAnyListItems ? (
            <Widget
              shouldHideShadow={true}
              backgroundColor="#F8F9FA"
              isEmpty={true}
              emptyState={{
                title: `No ${currentDay?.month} Reminders`,
                description:
                  'Birthdays and Anniversaries can be added to each of your contacts so you never forget a special occasion again!',
                icon: 'exp_calendar',
              }}
            />
          ) : (
            remindersToShow.map(reminder => {
              const name = `${reminder.contact.firstName} ${reminder.contact.lastName}`
              const type = reminder.type.toLowerCase()
              return (
                <ReminderListCard
                  key={reminder.id}
                  type={type}
                  name={name}
                  date={reminder.eventDate ?? ''}
                  onClick={() => setSelectedReminder(reminder)}
                />
              )
            })
          )}
          {/* TODO: Refactor this to have the dateBadge in the top center */}
          {selectedReminder && (
            <ConfirmDialog
              isOpen={!!selectedReminder}
              onClose={() => setSelectedReminder(undefined)}
              title={`It's Time to Celebrate ${
                selectedReminder.contact.firstName
              }'s ${
                selectedReminder.type.charAt(0).toUpperCase() +
                selectedReminder.type.slice(1).toLowerCase()
              }!`}
              description="Get creative and fun sending a card and gift that will surely make them feel special."
              primaryAction="accept"
              accept={{
                title: 'Create Card',
                onClick: () =>
                  handleCreateOrder(
                    selectedReminder.contact,
                    selectedReminder.id,
                  ),
              }}
              decline={{
                title: 'Send Later',
                onClick: () => setSelectedReminder(undefined),
              }}
            >
              {/* <DateBadge
            type={selectedReminder.type.toLowerCase()}
            date={selectedReminder.eventDate ?? ''}
          /> */}
            </ConfirmDialog>
          )}
          <Dialog
            isOpen={showNoRemindersDialog}
            onClose={() => setShowNoRemindersDialog(false)}
            title="Create or Edit Contact"
            maxWidth="520px"
          >
            <Flex flexDirection="column" rowGap="x2">
              <Text
                type="body"
                color="primaryBody"
                content="Add birthdays and anniversaries to your contacts so you never forget a special occasion again."
              />
              <Flex justifyContent="flex-end" columnGap="x1">
                <Button
                  title="Cancel"
                  textSize="12px"
                  padding="8px 20px"
                  height="38px"
                  background="#E8E8E8"
                  textColor="#424548"
                  onClick={() => setShowNoRemindersDialog(false)}
                />
                <Button
                  title="Go To Contacts"
                  textSize="12px"
                  padding="8px 20px"
                  height="38px"
                  background="#424548"
                  textColor="#fff"
                  onClick={() => {
                    setShowNoRemindersDialog(false)
                    actions.openRelationshipManager()
                  }}
                />
              </Flex>
            </Flex>
          </Dialog>
        </Flex>
        <Flex
          width="100%"
          columnGap="12px"
          style={{
            paddingTop: 16,
            marginTop: isVerticalLayout ? 'auto' : undefined,
          }}
        >
          <Flex
            justifyContent={shouldCenterAddButton ? 'center' : undefined}
            style={{ flex: 1 }}
          >
            <Button
              title={isVerticalLayout ? 'Reminder' : 'Add a reminder'}
              textSize="12px"
              padding="8px 28px"
              height="38px"
              background={hasAnyListItems ? '#424548' : '#BE9CDD'}
              width={shouldCenterAddButton ? undefined : '100%'}
              textColor="#fff"
              onClick={handleAddReminderClick}
              icon={
                isVerticalLayout
                  ? { name: 'plus_circle', color: '#fff', size: 21 }
                  : undefined
              }
            />
          </Flex>
          {shouldShowViewAllButton && (
            <Flex style={{ flex: 1 }}>
              <Button
                title="View All"
                textSize="12px"
                background="#BE9CDD"
                height="38px"
                width="100%"
                onClick={() => actions.openNotifications()}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}
